/**
 * WebAuthn utility library
 * Handles browser-side WebAuthn/FIDO2 operations
 */

export interface PublicKeyCredentialWithAttestation extends PublicKeyCredential {
    response: AuthenticatorAttestationResponse;
}

export interface PublicKeyCredentialWithAssertion extends PublicKeyCredential {
    response: AuthenticatorAssertionResponse;
}

export interface RegistrationOptions {
    challenge: string;
    rp: {
        name: string;
        id: string;
    };
    user: {
        id: string;
        name: string;
        displayName: string;
    };
    pubKeyCredParams: Array<{
        type: string;
        alg: number;
    }>;
    excludeCredentials?: Array<{
        type: string;
        id: string;
        transports?: string[];
    }>;
    authenticatorSelection?: {
        authenticatorAttachment?: string;
        requireResidentKey?: boolean;
        residentKey?: string;
        userVerification?: string;
    };
    timeout?: number;
    attestation?: string;
}

export interface AuthenticationOptions {
    challenge: string;
    rpId?: string;
    allowCredentials?: Array<{
        type: string;
        id: string;
        transports?: string[];
    }>;
    timeout?: number;
    userVerification?: string;
}

export interface RegistrationResponse {
    id: string;
    rawId: string;
    type: string;
    response: {
        clientDataJSON: string;
        attestationObject: string;
        transports?: string[];
    };
}

export interface AuthenticationResponse {
    id: string;
    rawId: string;
    type: string;
    response: {
        clientDataJSON: string;
        authenticatorData: string;
        signature: string;
        userHandle: string | null;
    };
}

export class WebAuthnError extends Error {
    constructor(
        message: string,
        public readonly code?: string
    ) {
        super(message);
        this.name = 'WebAuthnError';
    }
}

export class WebAuthn {
    /**
     * Check if WebAuthn is supported in this browser
     */
    public static isSupported(): boolean {
        return window.PublicKeyCredential !== undefined &&
               typeof window.PublicKeyCredential === 'function';
    }

    /**
     * Check if platform authenticator (e.g., TouchID, Windows Hello) is available
     */
    public static async isPlatformAuthenticatorAvailable(): Promise<boolean> {
        if (!WebAuthn.isSupported()) {
            return false;
        }

        try {
            return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        } catch (error) {
            console.error('Failed to check platform authenticator:', error);
            return false;
        }
    }

    /**
     * Check if Conditional UI (autofill passkeys) is available
     */
    public static async isConditionalMediationAvailable(): Promise<boolean> {
        if (!WebAuthn.isSupported()) {
            return false;
        }

        try {
            // Check if the browser supports conditional mediation
            if (typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') {
                return false;
            }
            return await PublicKeyCredential.isConditionalMediationAvailable();
        } catch (error) {
            console.error('Failed to check conditional mediation:', error);
            return false;
        }
    }

    /**
     * Convert base64url string to ArrayBuffer
     */
    private static base64urlToArrayBuffer(base64url: string): ArrayBuffer {
        // Convert base64url to base64
        const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

        // Pad with '=' if needed
        const paddedBase64 = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

        // Decode base64 to binary string
        const binaryString = atob(paddedBase64);

        // Convert binary string to ArrayBuffer
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes.buffer;
    }

    /**
     * Convert ArrayBuffer to base64url string
     */
    private static arrayBufferToBase64url(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        // Convert to base64
        const base64 = btoa(binary);

        // Convert base64 to base64url
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    /**
     * Register a new credential (create)
     */
    public static async register(
        options: RegistrationOptions,
        credentialName?: string
    ): Promise<RegistrationResponse> {
        if (!WebAuthn.isSupported()) {
            throw new WebAuthnError('WebAuthn is not supported in this browser');
        }

        try {
            // Convert options to browser format
            const publicKeyOptions: PublicKeyCredentialCreationOptions = {
                challenge: WebAuthn.base64urlToArrayBuffer(options.challenge),
                rp: options.rp,
                user: {
                    id: WebAuthn.base64urlToArrayBuffer(options.user.id),
                    name: options.user.name,
                    displayName: options.user.displayName,
                },
                pubKeyCredParams: options.pubKeyCredParams.map(param => ({
                    type: param.type as PublicKeyCredentialType,
                    alg: param.alg,
                })),
                excludeCredentials: options.excludeCredentials?.map(cred => ({
                    type: cred.type as PublicKeyCredentialType,
                    id: WebAuthn.base64urlToArrayBuffer(cred.id),
                    transports: cred.transports as AuthenticatorTransport[],
                })),
                timeout: options.timeout,
                attestation: options.attestation as AttestationConveyancePreference,
                authenticatorSelection: options.authenticatorSelection ? {
                    authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment,
                    requireResidentKey: options.authenticatorSelection.requireResidentKey,
                    residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement,
                    userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement,
                } : undefined,
            };

            // Debug logging - log what we're sending to the browser
            console.log('[WebAuthn] Calling navigator.credentials.create with:', {
                challenge: options.challenge.substring(0, 20) + '...',
                rpId: publicKeyOptions.rp.id,
                excludeCredentials: publicKeyOptions.excludeCredentials?.map(c => ({
                    type: c.type,
                    id: '[' + c.id.byteLength + ' bytes]',
                    transports: c.transports
                }))
            });

            // Call browser WebAuthn API
            const credential = await navigator.credentials.create({
                publicKey: publicKeyOptions,
            }) as PublicKeyCredentialWithAttestation | null;

            if (!credential) {
                throw new WebAuthnError('Failed to create credential');
            }

            // Extract transports if available
            const transports = credential.response.getTransports?.() || [];

            // Convert credential to serializable format
            const response: RegistrationResponse = {
                id: credential.id,
                rawId: WebAuthn.arrayBufferToBase64url(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: WebAuthn.arrayBufferToBase64url(credential.response.clientDataJSON),
                    attestationObject: WebAuthn.arrayBufferToBase64url(credential.response.attestationObject),
                    transports: transports.length > 0 ? transports : undefined,
                },
            };

            return response;

        } catch (error) {
            // Debug logging - log all errors to console
            console.error('[WebAuthn] Registration error:', {
                error,
                name: error instanceof Error ? error.name : 'unknown',
                message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
                excludeCredentialsProvided: !!options.excludeCredentials,
                excludeCredentialsCount: options.excludeCredentials?.length || 0
            });

            if (error instanceof Error) {
                if (error.name === 'NotAllowedError') {
                    // NotAllowedError can mean user cancelled OR authenticator was excluded
                    // Browser doesn't give us enough info to distinguish reliably
                    // Provide helpful message that covers both cases
                    throw new WebAuthnError(
                        'Registration was not completed. This may be because you cancelled the operation or this authenticator is already registered.',
                        'NOT_ALLOWED'
                    );
                } else if (error.name === 'InvalidStateError') {
                    throw new WebAuthnError('This authenticator is already registered', 'ALREADY_REGISTERED');
                } else if (error.name === 'NotSupportedError') {
                    throw new WebAuthnError('This authenticator is not supported', 'NOT_SUPPORTED');
                } else if (error.name === 'SecurityError') {
                    throw new WebAuthnError('Security error - HTTPS required', 'SECURITY_ERROR');
                }
                throw new WebAuthnError(error.message);
            }
            throw new WebAuthnError('Unknown error during registration');
        }
    }

    /**
     * Authenticate with Conditional UI (autofill passkeys)
     * This runs in the background and resolves when user selects a passkey from autofill
     */
    public static async authenticateConditional(
        options: AuthenticationOptions,
        signal?: AbortSignal
    ): Promise<AuthenticationResponse> {
        if (!WebAuthn.isSupported()) {
            throw new WebAuthnError('WebAuthn is not supported in this browser');
        }

        try {
            // Convert options to browser format - no allowCredentials for conditional UI
            const publicKeyOptions: PublicKeyCredentialRequestOptions = {
                challenge: WebAuthn.base64urlToArrayBuffer(options.challenge),
                rpId: options.rpId,
                timeout: options.timeout,
                userVerification: options.userVerification as UserVerificationRequirement,
                // Empty allowCredentials lets browser show all passkeys for this RP
                allowCredentials: [],
            };

            // Call browser WebAuthn API with conditional mediation
            const credential = await navigator.credentials.get({
                publicKey: publicKeyOptions,
                mediation: 'conditional',
                signal,
            }) as PublicKeyCredentialWithAssertion | null;

            if (!credential) {
                throw new WebAuthnError('Failed to get credential');
            }

            // Convert credential to serializable format
            const response: AuthenticationResponse = {
                id: credential.id,
                rawId: WebAuthn.arrayBufferToBase64url(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: WebAuthn.arrayBufferToBase64url(credential.response.clientDataJSON),
                    authenticatorData: WebAuthn.arrayBufferToBase64url(credential.response.authenticatorData),
                    signature: WebAuthn.arrayBufferToBase64url(credential.response.signature),
                    userHandle: credential.response.userHandle ?
                        WebAuthn.arrayBufferToBase64url(credential.response.userHandle) : null,
                },
            };

            return response;

        } catch (error) {
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new WebAuthnError('Authentication was aborted', 'ABORTED');
                } else if (error.name === 'NotAllowedError') {
                    throw new WebAuthnError('Authentication was cancelled or timed out', 'CANCELLED');
                } else if (error.name === 'SecurityError') {
                    throw new WebAuthnError('Security error - HTTPS required', 'SECURITY_ERROR');
                }
                throw new WebAuthnError(error.message);
            }
            throw new WebAuthnError('Unknown error during authentication');
        }
    }

    /**
     * Authenticate with existing credential (get)
     */
    public static async authenticate(
        options: AuthenticationOptions
    ): Promise<AuthenticationResponse> {
        if (!WebAuthn.isSupported()) {
            throw new WebAuthnError('WebAuthn is not supported in this browser');
        }

        try {
            // Convert options to browser format
            const publicKeyOptions: PublicKeyCredentialRequestOptions = {
                challenge: WebAuthn.base64urlToArrayBuffer(options.challenge),
                rpId: options.rpId,
                timeout: options.timeout,
                userVerification: options.userVerification as UserVerificationRequirement,
                allowCredentials: options.allowCredentials?.map(cred => ({
                    type: cred.type as PublicKeyCredentialType,
                    id: WebAuthn.base64urlToArrayBuffer(cred.id),
                    transports: cred.transports as AuthenticatorTransport[],
                })),
            };

            // Call browser WebAuthn API
            const credential = await navigator.credentials.get({
                publicKey: publicKeyOptions,
            }) as PublicKeyCredentialWithAssertion | null;

            if (!credential) {
                throw new WebAuthnError('Failed to get credential');
            }

            // Convert credential to serializable format
            const response: AuthenticationResponse = {
                id: credential.id,
                rawId: WebAuthn.arrayBufferToBase64url(credential.rawId),
                type: credential.type,
                response: {
                    clientDataJSON: WebAuthn.arrayBufferToBase64url(credential.response.clientDataJSON),
                    authenticatorData: WebAuthn.arrayBufferToBase64url(credential.response.authenticatorData),
                    signature: WebAuthn.arrayBufferToBase64url(credential.response.signature),
                    userHandle: credential.response.userHandle ?
                        WebAuthn.arrayBufferToBase64url(credential.response.userHandle) : null,
                },
            };

            return response;

        } catch (error) {
            if (error instanceof Error) {
                if (error.name === 'NotAllowedError') {
                    throw new WebAuthnError('Authentication was cancelled or timed out', 'CANCELLED');
                } else if (error.name === 'SecurityError') {
                    throw new WebAuthnError('Security error - HTTPS required', 'SECURITY_ERROR');
                } else if (error.name === 'NotFoundError') {
                    throw new WebAuthnError('No credential found for this device', 'NOT_FOUND');
                }
                throw new WebAuthnError(error.message);
            }
            throw new WebAuthnError('Unknown error during authentication');
        }
    }

    /**
     * Get user-friendly error message
     */
    public static getUserFriendlyErrorMessage(error: Error): string {
        if (error instanceof WebAuthnError) {
            switch (error.code) {
                case 'NOT_ALLOWED':
                case 'CANCELLED':
                    return error.message;
                case 'ALREADY_REGISTERED':
                    return 'This authenticator is already registered.';
                case 'NOT_SUPPORTED':
                    return 'Your device does not support this type of authentication.';
                case 'SECURITY_ERROR':
                    return 'Security error. Please ensure you are using HTTPS.';
                case 'NOT_FOUND':
                    return 'No registered authenticator found on this device.';
                default:
                    return error.message;
            }
        }
        return 'An unexpected error occurred. Please try again.';
    }
}
